import React, { useState } from 'react';
import { ArrowLeft, Linkedin, Sparkles, Loader2, Copy, Check, ChevronDown, ChevronUp, Lightbulb, User, Briefcase, Award, AlertCircle, RefreshCw, MessageSquare } from 'lucide-react';

function LinkedInProfileView({
  masterResume,
  hasMasterResume,
  formatMasterResume,
  authToken,
  onNavigate,
  onShowPaywall
}) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [copied, setCopied] = useState({});
  const [expandedSections, setExpandedSections] = useState({ about: true, experience: true, skills: true, tips: false });

  // Feedback state for each section
  const [feedback, setFeedback] = useState({});
  const [refining, setRefining] = useState({});
  const [showFeedback, setShowFeedback] = useState({});

  const handleCopy = async (text, sectionId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(prev => ({ ...prev, [sectionId]: true }));
      setTimeout(() => {
        setCopied(prev => ({ ...prev, [sectionId]: false }));
      }, 2000);

      if (window.posthog) {
        window.posthog.capture('linkedin_section_copied', { section: sectionId });
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleFeedback = (section) => {
    setShowFeedback(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleRefine = async (sectionType) => {
    if (!feedback[sectionType]?.trim()) return;

    setRefining(prev => ({ ...prev, [sectionType]: true }));

    try {
      const response = await fetch('/api/refine-linkedin-section', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          sectionType,
          currentContent: sectionType === 'headline' ? profile.headline :
                          sectionType === 'about' ? profile.about :
                          sectionType === 'skills' ? profile.skills : null,
          feedback: feedback[sectionType],
          masterResume: formatMasterResume(),
          targetRole: targetRole.trim() || undefined
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(`Server error (${response.status}). Please try again.`);
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to refine section');
      }

      // Update the specific section in profile
      setProfile(prev => ({
        ...prev,
        [sectionType]: data.refined
      }));

      // Clear feedback after successful refinement
      setFeedback(prev => ({ ...prev, [sectionType]: '' }));
      setShowFeedback(prev => ({ ...prev, [sectionType]: false }));

      if (window.posthog) {
        window.posthog.capture('linkedin_section_refined', {
          section: sectionType,
          feedback_length: feedback[sectionType].length
        });
      }
    } catch (err) {
      console.error('Error refining section:', err);
      setError(err.message || 'Failed to refine section');
    } finally {
      setRefining(prev => ({ ...prev, [sectionType]: false }));
    }
  };

  const handleGenerate = async () => {
    if (!hasMasterResume) {
      setError('Please add your master resume first');
      return;
    }

    setIsGenerating(true);
    setError('');
    setProfile(null);

    try {
      const response = await fetch('/api/generate-linkedin-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          masterResume: formatMasterResume(),
          targetRole: targetRole.trim() || undefined
        })
      });

      if (response.status === 402) {
        onShowPaywall();
        return;
      }

      // Check content type to handle non-JSON responses (like 404 HTML pages)
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(`Server error (${response.status}). Please try again.`);
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate LinkedIn profile');
      }
      setProfile(data);

      if (window.posthog) {
        window.posthog.capture('linkedin_profile_generated', {
          target_role: targetRole || 'none',
          headline_length: data.headline?.charCount || 0,
          about_length: data.about?.charCount || 0
        });
      }
    } catch (err) {
      console.error('Error generating LinkedIn profile:', err);
      setError(err.message || 'Failed to generate LinkedIn profile');

      if (window.posthog) {
        window.posthog.captureException(err, { component: 'LinkedInProfileView' });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const CharacterCount = ({ count, max }) => (
    <div className="flex items-center gap-1.5 text-xs mt-2">
      <span className={count > max ? 'text-red-500 font-medium' : 'text-slate-500'}>
        {count?.toLocaleString()}/{max.toLocaleString()} characters
      </span>
      {count <= max && <Check size={12} className="text-green-500" />}
      {count > max && <AlertCircle size={12} className="text-red-500" />}
    </div>
  );

  const CopyButton = ({ text, sectionId, small = false }) => (
    <button
      onClick={() => handleCopy(text, sectionId)}
      className={`flex items-center gap-1.5 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 ${
        small ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
      }`}
    >
      {copied[sectionId] ? <Check size={small ? 12 : 14} className="text-green-600" /> : <Copy size={small ? 12 : 14} />}
      {copied[sectionId] ? 'Copied!' : 'Copy'}
    </button>
  );

  const RefineButton = ({ section }) => (
    <button
      onClick={() => toggleFeedback(section)}
      className={`flex items-center gap-1.5 text-sm font-medium rounded-xl transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 px-3 py-1.5 ${
        showFeedback[section]
          ? 'bg-primary/10 text-primary'
          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
      }`}
    >
      <MessageSquare size={14} />
      Refine
    </button>
  );

  // Helper to render feedback input inline (not as component to avoid focus issues)
  const renderFeedbackInput = (section, placeholder) => {
    if (!showFeedback[section]) return null;
    return (
      <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
        <label className="block text-xs font-medium text-slate-600 mb-2">
          What would you like to change?
        </label>
        <textarea
          value={feedback[section] || ''}
          onChange={(e) => setFeedback(prev => ({ ...prev, [section]: e.target.value }))}
          placeholder={placeholder}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none"
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={() => handleRefine(section)}
            disabled={!feedback[section]?.trim() || refining[section]}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
              feedback[section]?.trim() && !refining[section]
                ? 'bg-primary text-white hover:bg-dark'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {refining[section] ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Refining...
              </>
            ) : (
              <>
                <RefreshCw size={14} />
                Regenerate
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  const SectionHeader = ({ icon: Icon, title, section, copyText, copyId }) => (
    <div className="flex items-center justify-between mb-4">
      <button
        onClick={() => toggleSection(section)}
        className="flex items-center gap-2 text-left cursor-pointer hover:text-primary transition-colors"
      >
        <Icon size={20} className="text-primary" />
        <h3 className="font-heading text-lg font-semibold text-slate-900">{title}</h3>
        {expandedSections[section] ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>
      {copyText && expandedSections[section] && <CopyButton text={copyText} sectionId={copyId} />}
    </div>
  );

  // Pre-generation view
  if (!profile && !isGenerating) {
    return (
      <div className="animate-fadeIn">
        <button
          onClick={() => onNavigate('dashboard')}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-primary transition-colors cursor-pointer mb-6"
        >
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>

        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0A66C2]/10 rounded-2xl mb-4">
              <Linkedin size={32} className="text-[#0A66C2]" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
              LinkedIn Profile Optimizer
            </h1>
            <p className="text-slate-600">
              Transform your resume into a compelling LinkedIn profile that attracts recruiters and connections
            </p>
          </div>

          {/* Input Card */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Target Role (optional)
            </label>
            <input
              type="text"
              value={targetRole}
              onChange={(e) => setTargetRole(e.target.value)}
              placeholder="e.g., Senior Product Manager, Staff Engineer"
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
            <p className="text-xs text-slate-500 mt-2">
              Specify a target role to optimize keywords for LinkedIn search
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-start gap-3">
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={!hasMasterResume}
            className={`w-full px-6 py-4 font-medium rounded-xl transition-colors flex items-center justify-center gap-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
              hasMasterResume
                ? 'bg-primary text-white hover:bg-dark'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            <Sparkles size={20} />
            Generate LinkedIn Profile
          </button>

          {!hasMasterResume && (
            <p className="text-center text-sm text-amber-600 mt-3">
              Add your master resume first to generate LinkedIn content
            </p>
          )}

          {/* What You'll Get */}
          <div className="mt-8 grid grid-cols-2 gap-4">
            {[
              { icon: User, label: 'Headline', desc: 'Eye-catching tagline (120 chars)' },
              { icon: Briefcase, label: 'About Section', desc: 'Compelling bio with hooks' },
              { icon: Award, label: 'Experience Bullets', desc: 'Optimized for each role' },
              { icon: Lightbulb, label: 'Pro Tips', desc: 'Profile optimization advice' }
            ].map((item, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-4">
                <item.icon size={20} className="text-primary mb-2" />
                <div className="font-medium text-slate-900 text-sm">{item.label}</div>
                <div className="text-xs text-slate-500">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Loading view
  if (isGenerating) {
    return (
      <div className="animate-fadeIn">
        <button
          onClick={() => onNavigate('dashboard')}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-primary transition-colors cursor-pointer mb-6"
        >
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>

        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-6">
            <Loader2 size={32} className="text-primary animate-spin" />
          </div>
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">
            Crafting Your LinkedIn Profile
          </h2>
          <p className="text-slate-600 mb-6">
            Analyzing your experience and optimizing for LinkedIn's algorithm...
          </p>
          <div className="h-2 bg-slate-200 rounded-full max-w-xs mx-auto overflow-hidden">
            <div className="h-full bg-primary rounded-full animate-pulse" style={{ width: '60%' }} />
          </div>
        </div>
      </div>
    );
  }

  // Results view
  return (
    <div className="animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => onNavigate('dashboard')}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-primary transition-colors cursor-pointer"
        >
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>
        <button
          onClick={() => setProfile(null)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-dark transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          <Sparkles size={16} />
          Generate New
        </button>
      </div>

      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-xl mb-3">
            <Check size={24} className="text-green-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Your LinkedIn Profile is Ready
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Copy each section to update your LinkedIn profile
          </p>
        </div>

        {/* Headline Section */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <User size={20} className="text-primary" />
              <h3 className="font-heading text-lg font-semibold text-slate-900">Headline</h3>
            </div>
            <div className="flex items-center gap-2">
              <RefineButton section="headline" />
              <CopyButton text={profile.headline?.text || ''} sectionId="headline" />
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-slate-900 font-medium">{profile.headline?.text}</p>
          </div>
          <CharacterCount count={profile.headline?.charCount} max={120} />
          {profile.headline?.keywords?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {profile.headline.keywords.map((kw, i) => (
                <span key={i} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-lg">
                  {kw}
                </span>
              ))}
            </div>
          )}
          {renderFeedbackInput('headline', "e.g., Remove 'ex-SpaceX', make it less salesy, focus more on leadership...")}
        </div>

        {/* About Section */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => toggleSection('about')}
              className="flex items-center gap-2 text-left cursor-pointer hover:text-primary transition-colors"
            >
              <Briefcase size={20} className="text-primary" />
              <h3 className="font-heading text-lg font-semibold text-slate-900">About</h3>
              {expandedSections.about ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
            </button>
            {expandedSections.about && (
              <div className="flex items-center gap-2">
                <RefineButton section="about" />
                <CopyButton text={profile.about?.text || ''} sectionId="about" />
              </div>
            )}
          </div>
          {expandedSections.about && (
            <>
              {profile.about?.hookPreview && (
                <div className="mb-4">
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                    Hook (first 3 lines - visible before "see more")
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-slate-900 text-sm whitespace-pre-wrap">{profile.about.hookPreview}</p>
                  </div>
                </div>
              )}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-slate-700 text-sm whitespace-pre-wrap leading-relaxed">{profile.about?.text}</p>
              </div>
              <CharacterCount count={profile.about?.charCount} max={2600} />
              {renderFeedbackInput('about', "e.g., Make the hook more attention-grabbing, add more personality, shorten it...")}
            </>
          )}
        </div>

        {/* Experience Section */}
        {profile.experience?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <SectionHeader icon={Award} title="Experience Bullets" section="experience" />
            {expandedSections.experience && (
              <div className="space-y-4">
                {profile.experience.map((exp, i) => (
                  <div key={i} className="border border-slate-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="font-medium text-slate-900">{exp.company}</div>
                        <div className="text-sm text-slate-600">{exp.title}</div>
                      </div>
                      <CopyButton
                        text={exp.bullets?.join('\n• ') || ''}
                        sectionId={`exp-${i}`}
                        small
                      />
                    </div>
                    <ul className="space-y-2">
                      {exp.bullets?.map((bullet, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="text-primary mt-1">•</span>
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Skills Section */}
        {profile.skills && (
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => toggleSection('skills')}
                className="flex items-center gap-2 text-left cursor-pointer hover:text-primary transition-colors"
              >
                <Award size={20} className="text-primary" />
                <h3 className="font-heading text-lg font-semibold text-slate-900">Skills to Feature</h3>
                {expandedSections.skills ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
              </button>
              {expandedSections.skills && (
                <div className="flex items-center gap-2">
                  <RefineButton section="skills" />
                  <CopyButton text={[...(profile.skills.featured || []), ...(profile.skills.recommended || [])].join(', ')} sectionId="skills" />
                </div>
              )}
            </div>
            {expandedSections.skills && (
              <>
                {profile.skills.featured?.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                      Featured (Pin these top 5)
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {profile.skills.featured.map((skill, i) => (
                        <span key={i} className="px-3 py-1.5 bg-primary text-white text-sm rounded-lg font-medium">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {profile.skills.recommended?.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                      Also Add
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {profile.skills.recommended.map((skill, i) => (
                        <span key={i} className="px-3 py-1.5 bg-slate-100 text-slate-700 text-sm rounded-lg">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {renderFeedbackInput('skills', "e.g., Add more technical skills, remove soft skills, focus on cloud technologies...")}
              </>
            )}
          </div>
        )}

        {/* Tips Section */}
        {profile.tips?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <SectionHeader icon={Lightbulb} title="Optimization Tips" section="tips" />
            {expandedSections.tips && (
              <ul className="space-y-3">
                {profile.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-medium">
                      {i + 1}
                    </span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Credit Info */}
        <div className="text-center text-xs text-slate-500 pt-4">
          Credits used: {profile.creditsUsed} / {profile.freeLimit + (profile.paidCredits || 0)}
        </div>
      </div>
    </div>
  );
}

export default LinkedInProfileView;
